import React, { FC } from 'react';
import { Campaign } from '@shared/types/mylounge';
import { useGridLayoutContext } from '../context/GridLayoutContext';
import { Slot, SlotSize } from './CampaignBlock';
import { StyledOpenCampaignCard } from '../styles';

interface FillRemainingProps {
    slot?: SlotSize;
    customCampaignsList?: Campaign[];
    /**
     * Starting index for campaigns in this block.
     * Explicitly passed to avoid mutable state in context.
     * Default: 0 (will be set by DeclarativeGrid during traversal)
     */
    startIndex?: number;
    /**
     * Optional row count for calculating how many campaigns to render.
     * If provided, will render startIndex + (rowCount * columnCount) campaigns.
     * If not provided, will render all remaining campaigns.
     */
    rowCount?: number;
}

export const FillRemaining: FC<FillRemainingProps> = ({
    slot = Slot.SQUARE,
    customCampaignsList,
    startIndex = 0,
    rowCount,
}) => {
    const { campaigns: campaignFromContext, isPlusEnabled, columnCount } = useGridLayoutContext();
    const items: React.ReactElement[] = [];

    const { aspect, colSpan, rowSpan } = slot;
    const campaigns = customCampaignsList ?? campaignFromContext;

    /**
     * Calculate end index:
     * - If rowCount provided: startIndex + (rowCount * columnCount)
     * - Otherwise: length of campaigns (render all remaining)
     */
    const endIndex = rowCount ? startIndex + rowCount * columnCount : campaigns.length;

    for (
        let currentIndex = startIndex;
        currentIndex < endIndex && currentIndex < campaigns.length;
        currentIndex++
    ) {
        const campaign = campaigns[currentIndex];
        if (!campaign) break;

        items.push(
            <StyledOpenCampaignCard
                key={campaign.campaignId}
                $colSpan={colSpan}
                $rowSpan={rowSpan}
                aspectRatio={aspect}
                campaign={campaign}
                isPlusEnabled={isPlusEnabled}
                position={`${currentIndex + 1}|${columnCount}`}
            />,
        );
    }

    return items.length > 0 ? items : null;
};
